/* assets/cart-spa.js. Boom - Cart Speed.
 *
 * Intercepts add-to-cart form submits, posts to /cart/add.js with the
 * Shopify Section Rendering API, then swaps the main content area to the
 * rendered /cart section with a 150ms fade. pushState updates the URL to
 * /cart so the user sees the cart page without a full navigation.
 *
 * Back-button strategy (deliberate):
 * Going FORWARD (ATC -> cart) we swap content in-place. Fast, no reload.
 * Going BACK (cart -> source page) we trigger a real `location.replace` so
 * the source page reloads with all its event listeners intact (variant
 * selector, image carousels, etc.). The pre-ATC scroll position is stored
 * in `history.state` and restored on page load.
 *
 * Cost: back navigation isn't instant, but everything on the PDP works.
 * Cheaper than the alternative: re-injecting <script> nodes re-declares
 * theme globals (`let DT_DISCOUNT = ...`) and silently breaks widgets.
 *
 * Falls back to a normal form submit on any error.
 */
(function () {
  'use strict';
  if (!window.fetch || !window.history || !window.history.pushState) return;
  if (window.__cartSpaLoaded) return;
  window.__cartSpaLoaded = true;

  var CART_SECTION = 'main-cart-template';
  var CONTAINER_ID = 'container';
  var FADE_MS = 150;
  var SCROLL_KEY = '__cartSpaScroll';

  function getContainer() {
    return document.getElementById(CONTAINER_ID);
  }

  function isOnCartPath() {
    return /^\/cart(\/|\?|$)/.test(location.pathname + (location.search || ''));
  }

  function updateHeaderCount(count) {
    var nodes = document.querySelectorAll('.CartCount');
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = String(count);
    var wrap = document.getElementById('CartCount');
    if (wrap) {
      wrap.classList.toggle('xhide', count === 0);
      wrap.setAttribute('data-cart_item_count', String(count));
    }
  }

  // Re-execute <script> nodes inside HTML inserted via innerHTML.
  // Used only for the cart section, where scripts are fresh and idempotent.
  function executeScripts(root) {
    var scripts = root.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var oldScript = scripts[i];
      var newScript = document.createElement('script');
      for (var j = 0; j < oldScript.attributes.length; j++) {
        var a = oldScript.attributes[j];
        newScript.setAttribute(a.name, a.value);
      }
      newScript.text = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    }
  }

  // Re-initialize Shopify's express checkout buttons (Shop Pay, Google Pay,
  // Apple Pay) after cart section is injected via innerHTML. Modern Shopify
  // uses the <shopify-accelerated-checkout-cart> custom element which auto-
  // upgrades when inserted, but it also listens for shopify:section:load
  // events for dynamic-section refreshes (Section Rendering API).
  function reinitPaymentButtons(container) {
    try {
      // Older API: re-scan classic payment buttons
      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        window.Shopify.PaymentButton.init();
      }
    } catch (_) {}
    try {
      // Modern API: dispatch the event Shopify themes use to signal
      // a section was re-rendered (per Section Rendering API docs)
      var section = container.querySelector('[data-section-id], [data-section-type]');
      var sectionId = section
        ? (section.getAttribute('data-section-id') || section.getAttribute('data-section-type'))
        : 'main-cart-template';
      document.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: sectionId, load: true },
      }));
    } catch (_) {}
  }

  function fadeSwap(container, newHtml) {
    return new Promise(function (resolve) {
      container.style.transition = 'opacity ' + FADE_MS + 'ms ease';
      container.style.opacity = '0';
      window.setTimeout(function () {
        container.innerHTML = newHtml;
        executeScripts(container);
        reinitPaymentButtons(container);
        void container.offsetHeight;
        container.style.opacity = '1';
        window.setTimeout(function () {
          container.style.transition = '';
          resolve();
        }, FADE_MS);
      }, FADE_MS);
    });
  }

  function showError(form, message) {
    var existing = form.querySelector('.cart-spa-error');
    if (existing) existing.parentNode.removeChild(existing);
    var div = document.createElement('div');
    div.className = 'cart-spa-error';
    div.setAttribute('role', 'alert');
    div.style.cssText =
      'margin: 12px 0; padding: 10px 14px; background: #fef2f2; ' +
      'border: 1px solid #fecaca; border-radius: 6px; color: #991b1b; ' +
      'font-size: 14px;';
    div.textContent = message;
    form.appendChild(div);
    window.setTimeout(function () {
      if (div.parentNode) div.parentNode.removeChild(div);
    }, 5000);
  }

  function ajaxAdd(form) {
    var submitBtn = form.querySelector('button[type=submit], input[type=submit]');
    var wasDisabled = submitBtn ? submitBtn.disabled : false;
    if (submitBtn) submitBtn.disabled = true;
    form.classList.add('is-cart-spa-submitting');

    var fd = new FormData(form);
    fd.append('sections', CART_SECTION);
    fd.append('sections_url', '/cart');

    return fetch('/cart/add.js', {
      method: 'POST',
      body: fd,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then(function (addRes) {
      if (!addRes.ok) {
        return addRes.json().catch(function () { return {}; }).then(function (errBody) {
          var msg = errBody.description || errBody.message || 'Could not add to cart. Please try again.';
          showError(form, msg);
          return null;
        });
      }
      return addRes.json();
    }).then(function (addBody) {
      if (!addBody) return null;

      var countPromise = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { item_count: 1 }; })
        .catch(function () { return { item_count: 1 }; });

      var cartHtmlPromise;
      if (addBody.sections && addBody.sections[CART_SECTION]) {
        cartHtmlPromise = Promise.resolve(addBody.sections[CART_SECTION]);
      } else {
        cartHtmlPromise = fetch('/cart?section_id=' + CART_SECTION, { headers: { 'Accept': 'text/html' } })
          .then(function (r) { return r.text(); });
      }

      return Promise.all([countPromise, cartHtmlPromise]).then(function (results) {
        var cartJson = results[0];
        var cartHtml = results[1];
        var container = getContainer();
        if (!container) {
          location.assign('/cart');
          return null;
        }

        // Save current scroll into the SOURCE page's history entry so it
        // survives the reload that happens on back-button pop.
        var scrollY = window.scrollY || window.pageYOffset || 0;
        var sourceUrl = location.href;
        try {
          var existingState = history.state || {};
          history.replaceState(
            Object.assign({}, existingState, { __cartSpaScrollY: scrollY }),
            document.title,
            sourceUrl
          );
        } catch (_) {}

        // Push /cart entry. Mark it so popstate can identify the back transition.
        try {
          history.pushState(
            { __cartSpaCartOverlay: true, sourceUrl: sourceUrl, ts: Date.now() },
            'Cart',
            '/cart'
          );
        } catch (e) {
          location.assign('/cart');
          return null;
        }
        document.title = 'Cart';
        updateHeaderCount(cartJson.item_count || 0);

        return fadeSwap(container, cartHtml).then(function () {
          window.scrollTo(0, 0);
          var heading = container.querySelector('.cart-page__title, .cart-empty__title');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            try { heading.focus({ preventScroll: true }); }
            catch (_) { heading.focus(); }
          }
        });
      });
    }).catch(function (err) {
      try { if (window.console) console.warn('cart-spa: falling back to real submit', err); } catch (_) {}
      try {
        form.setAttribute('data-cart-spa-bypass', '1');
        form.submit();
      } catch (_) {
        location.assign('/cart');
      }
    }).then(function () {
      if (submitBtn) submitBtn.disabled = wasDisabled;
      form.classList.remove('is-cart-spa-submitting');
    });
  }

  function handlePopState(event) {
    var state = event && event.state;

    // Going BACK from cart overlay: real reload of source page.
    // Reloading (vs in-place innerHTML restore) keeps all event listeners
    // wired by the original page-load scripts (variant selector, carousels,
    // theme jQuery plugins). Far more reliable than re-injecting scripts.
    // The pre-ATC scrollY is in history.state for the source page entry,
    // so the early-init at the top of this script will restore it after reload.
    if (!isOnCartPath() && document.querySelector('.cart-page')) {
      // We were on /cart-overlay, now URL is back to source page → reload.
      location.reload();
      return;
    }

    // Going FORWARD from source page back into /cart (after a back). The
    // DOM is the source page but URL is /cart. Reload to fetch /cart fresh.
    if (isOnCartPath() && !document.querySelector('.cart-page')) {
      location.reload();
      return;
    }
  }

  // Restore scroll if we just reloaded a page that was previously snapshotted.
  function restoreScrollFromHistory() {
    try {
      var st = history.state;
      if (st && typeof st.__cartSpaScrollY === 'number' && st.__cartSpaScrollY > 0) {
        var y = st.__cartSpaScrollY;
        // Two RAFs. iOS Safari occasionally drops the first scrollTo on load
        requestAnimationFrame(function () {
          window.scrollTo(0, y);
          requestAnimationFrame(function () { window.scrollTo(0, y); });
        });
        // Clear so it doesn't restore on subsequent reloads of the same URL
        var fresh = Object.assign({}, st);
        delete fresh.__cartSpaScrollY;
        history.replaceState(fresh, document.title, location.href);
      }
    } catch (_) {}
  }

  function init() {
    // Manual scroll handling. We restore from history.state below
    try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (_) {}

    restoreScrollFromHistory();

    window.addEventListener('popstate', handlePopState);

    if (isOnCartPath()) return; // no ATC form to intercept

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches) return;
      if (form.hasAttribute('data-cart-spa-bypass')) return;
      if (!form.matches('form[action*="/cart/add"]')) return;
      if (isOnCartPath()) return;
      e.preventDefault();
      ajaxAdd(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
